'use client';

import dynamic from 'next/dynamic';

import { ResourceSuccessPageProps } from '@/app/[locale]/(leads-forms)/lp/thank-you/_components/ResourceSuccessPage';
import BasicCardListModule from '@/components/modules/BasicCardListModule/BasicCardListModule';
import CardListModule from '@/components/modules/CardListModule/CardListModule';
import {
  ApplicationFormData,
  FormWithBackgroundData,
  FormWithBackgroundType,
  RegularFormData,
} from '@/components/modules/types';
import { useRouter } from '@/hooks/useRouter';
import { MarketingModuleDto } from '@/lib/marketing-page/dto/modules';
import { BackgroundType } from '@/lib/marketing-page/dto/modules/shared';
import {
  isBasicCardListModule,
  isBlogPostsModule,
  isDataTableModule,
  isFinePrintModule,
  isFormWithBackgroundModule,
  isHeroResourceModule,
  isHeroWithRightImageModule,
  isImageLeftTextRightModule,
  isItemListModule,
  isLinkCardListModule,
  isNearbyLocationsModule,
  isPrincipalCustomersModule,
  isProductCardListModule,
  isQnAsModule,
  isQuoteModule,
  isSimpleHeroModule,
} from '@/lib/marketing-page/type-guards';
import { isCardListModule } from '@/lib/marketing-page/type-guards/card-list-type-guard';
import { isLinkListModule } from '@/lib/marketing-page/type-guards/link-list-module-type-guard';
import { isSocialReviewCardsModule } from '@/lib/marketing-page/type-guards/social-review-cards-module-type-guard';
import { PageActions } from '@/lib/types';
import { MarketingPageProps } from '@/types/marketing-pages';
import * as gtm from '@/utils/gtm';
import { stringifyUrl } from '@/utils/stringify-url';

const FormWithBackgroundModule = dynamic(
  () =>
    import(
      '@/components/modules/FormWithBackgroundModule/FormWithBackgroundModule'
    ),
  { ssr: false }
);

const NearbyLocationsModule = dynamic(
  () =>
    import('@/components/modules/NearbyLocationsModule/NearbyLocationsModule'),
  { ssr: false }
);

const ProductCardListModule = dynamic(
  () =>
    import('@/components/modules/ProductCardListModule/ProductCardListModule'),
  { ssr: false }
);

const ItemListModule = dynamic(
  () => import('@/components/modules/ItemListModule/ItemListModule'),
  { ssr: false }
);

const FinePrintModule = dynamic(
  () => import('@/components/modules/FinePrintModule/FinePrintModule'),
  { ssr: false }
);

const HeroWithRighImageModule = dynamic(
  () =>
    import(
      '@/components/modules/HeroWithRightImageModule/HeroWithRightImageModule'
    ),
  { ssr: false }
);

const SocialReviewCardsModule = dynamic(
  () =>
    import(
      '@/components/modules/SocialReviewCardsModule/SocialReviewCardsModule'
    ),
  { ssr: false }
);

const ImageLeftTextRightModule = dynamic(
  () =>
    import(
      '@/components/modules/ImageLeftTextRightModule/ImageLeftTextRightModule'
    ),
  { ssr: false }
);

const DataTableModule = dynamic(
  () => import('@/components/modules/DataTableModule/DataTableModule'),
  { ssr: false }
);

const HeroResourceModule = dynamic(
  () => import('@/components/modules/HeroResourceModule/HeroResourceModule'),
  { ssr: false }
);

const BlogPostsModule = dynamic(
  () => import('@/components/modules/BlogPostsModule/BlogPostsModule'),
  { ssr: false }
);

const LinkCardListModule = dynamic(
  () => import('@/components/modules/LinkCardListModule/LinkCardListModule'),
  { ssr: false }
);

const QuoteModule = dynamic(
  () => import('@/components/modules/QuoteModule/QuoteModule'),
  { ssr: false }
);

const QnAsModule = dynamic(
  () => import('@/components/modules/QnAsModule/QnAsModule'),
  { ssr: false }
);

const PrincipalCustomersModule = dynamic(
  () =>
    import(
      '@/components/modules/PrincipalCustomersModule/PrincipalCustomersModule'
    ),
  { ssr: false }
);

const LinkListModule = dynamic(
  () => import('@/components/modules/LinkListModule/LinkListModule'),
  { ssr: false }
);

const SimpleHeroModule = dynamic(
  () => import('@/components/modules/SimpleHeroModule/SimpleHeroModule'),
  { ssr: false }
);

type ModuleFactoryProps = MarketingPageProps & {
  module: MarketingModuleDto;
  index: number;
  mapboxAccessToken: string;
  onSubmit?: (
    form: FormWithBackgroundData,
    formType: FormWithBackgroundType
  ) => void;
  onFormChange?: (form: ApplicationFormData | RegularFormData) => void;
  onCTAClicked?: (action: PageActions) => void;
  productListTranslationKey?: string;
  productType?: string;
  productListShowTag?: boolean;
  valuesToBeReplacedObj?: Record<string, string>;
  linkListMaxItemsToShow?: number;
};

export default function ModuleFactory({
  onSubmit,
  onFormChange,
  onCTAClicked,
  index,
  module,
  mapboxAccessToken,
  productListTranslationKey,
  productType,
  productListShowTag,
  valuesToBeReplacedObj,
  linkListMaxItemsToShow,
  ...pageProps
}: ModuleFactoryProps) {
  const router = useRouter();

  if (isFormWithBackgroundModule(module) && onSubmit && onFormChange) {
    const { background, ...moduleProps } = module;
    return (
      <FormWithBackgroundModule
        isBackgroundCoverNavigationBar={module.isBackgroundCoverNavigationBar}
        locations={pageProps.locations ?? []}
        locationSlugs={pageProps.locationSlugs}
        phases={pageProps.phases}
        promoCode={pageProps.promoCode}
        background={{
          ...background,
          ...(background?.type === BackgroundType.Image &&
            index === 0 && {
              image: {
                ...background.image,
                priority: true,
              },
            }),
        }}
        {...moduleProps}
        onSubmit={onSubmit}
        onChange={onFormChange}
      />
    );
  }

  if (isItemListModule(module)) {
    return <ItemListModule {...module} />;
  }

  if (isNearbyLocationsModule(module)) {
    return (
      <NearbyLocationsModule
        title={module.title}
        backgroundColor={module.background?.color}
        mapboxAccessToken={mapboxAccessToken}
        slugs={pageProps.locationSlugs}
        mapHidden={module.mapHidden}
      />
    );
  }

  if (isProductCardListModule(module)) {
    return (
      <ProductCardListModule
        title={module.title}
        products={module.productTypes}
        backgroundColor={module.background?.color}
        translationKey={productListTranslationKey}
        showTag={productListShowTag}
        {...module}
      />
    );
  }

  if (isFinePrintModule(module)) {
    return <FinePrintModule {...module} />;
  }

  if (isHeroWithRightImageModule(module)) {
    return <HeroWithRighImageModule onCTAClicked={onCTAClicked} {...module} />;
  }

  if (isSocialReviewCardsModule(module)) {
    return (
      <SocialReviewCardsModule
        title={module.title}
        backgroundColor={module.background?.color}
      />
    );
  }

  if (isBasicCardListModule(module)) {
    return (
      <BasicCardListModule
        {...module}
        valueToBeReplacedObj={valuesToBeReplacedObj}
      />
    );
  }

  if (isImageLeftTextRightModule(module)) {
    return (
      <ImageLeftTextRightModule
        {...module}
        valueToBeReplacedObj={valuesToBeReplacedObj}
      />
    );
  }

  if (isDataTableModule(module)) {
    return <DataTableModule {...module} />;
  }

  if (isHeroResourceModule(module)) {
    return (
      <HeroResourceModule
        {...module}
        slug={pageProps.slug}
        locations={pageProps.locations ?? []}
        isBackgroundCoverNavigationBar={
          pageProps.isNavigationTransparent && index === 0
        }
        onFormSubmitted={(form) => {
          gtm.pushEvent('resourceFormSubmission');
          const assetName = `${pageProps.slug}.pdf`;
          const successPageProps: ResourceSuccessPageProps = {
            firstName: form.firstName,
            assetName,
            assetUrl: module.resourceUrl,
          };

          router.push(stringifyUrl('/lp/thank-you', successPageProps));
        }}
      />
    );
  }

  if (isBlogPostsModule(module)) {
    return <BlogPostsModule {...module} />;
  }

  if (isLinkCardListModule(module)) {
    return <LinkCardListModule {...module} />;
  }

  if (isQuoteModule(module)) {
    return <QuoteModule {...module} />;
  }

  if (isCardListModule(module)) {
    return (
      <CardListModule
        {...module}
        valueToBeReplacedObj={valuesToBeReplacedObj}
        productType={productType}
      />
    );
  }

  if (isSimpleHeroModule(module)) {
    return (
      <SimpleHeroModule
        {...module}
        valuesToBeReplaced={valuesToBeReplacedObj}
      />
    );
  }

  if (isPrincipalCustomersModule(module)) {
    return <PrincipalCustomersModule {...module} />;
  }

  if (isQnAsModule(module)) {
    return (
      <QnAsModule {...module} valuesToBeReplaced={valuesToBeReplacedObj} />
    );
  }

  if (isLinkListModule(module)) {
    return (
      <LinkListModule
        {...module}
        valueToBeReplacedObj={valuesToBeReplacedObj}
        maxItemsToShow={linkListMaxItemsToShow}
      />
    );
  }

  return null;
}
